import classNames from 'classnames';
import { BlockEmbed } from 'quill/blots/block';
import Embed from 'quill/blots/embed';
import type Header from 'quill/formats/header';
import React from 'react';
import { createPortal } from 'react-dom';
import SVGIcon from '../components/SVGIcon';
import Tooltip from '../components/Tooltip';
import alertIcon from '../icons/non-prefilled/16x16/alert.svg';
import * as styles from './BlotErrorBoundaryFallback.module.scss';
import type { PortalBlot } from './QuillEditor';
import type { ErrorBoundaryType } from '../components/ErrorBoundary';

function getMessage(type: ErrorBoundaryType) {
  switch (type) {
    case 'translation':
      return 'The error\nis most likely caused by\na translation tool.';
    case 'unsupportedBrowser':
      return 'Your browser\nmay be outdated or not\nsupported by Slab.';
    default:
      return 'The error\nhas been logged and\nwe’ll be on it!';
  }
}

interface BlotErrorBoundaryFallbackProps {
  blot: Embed | BlockEmbed | Header | PortalBlot;
  type: ErrorBoundaryType;
}

const BlotErrorBoundaryFallback = ({
  blot,
  type,
}: BlotErrorBoundaryFallbackProps) => {
  const title = 'Uh oh!';
  const message = `This ${
    blot.statics.blotName
  } is having\nissues loading. ${getMessage(type)}`;

  if (blot instanceof BlockEmbed) {
    return createPortal(
      <div className={classNames(styles.container, styles.block)}>
        <SVGIcon className={styles.icon}>{alertIcon}</SVGIcon>
        <div className={styles.content}>
          {title}
          <div className={styles.message}>{message}</div>
        </div>
      </div>,
      blot.domNode,
    );
  }

  if (blot instanceof Embed) {
    return createPortal(
      <Tooltip trigger="clickOrHover" content={message}>
        <div className={styles.container}>
          <SVGIcon inline className={styles.icon}>
            {alertIcon}
          </SVGIcon>
          <div className={styles.content}>{title}</div>
        </div>
      </Tooltip>,
      blot.contentNode,
    );
  }

  return null;
};

export default BlotErrorBoundaryFallback;
